import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UploadCloud, FileVideo, Loader2, RotateCw, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const LANG_OPTIONS = [
  { code: "auto", name: "Auto-detect" },
  { code: "en", name: "English" },
  { code: "ml", name: "Malayalam" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "hi", name: "Hindi" },
];

// Cloudflare enforces a ~100 MB body limit and ~100 s idle timeout. 10 MB chunks
// stay well below both. 4 parallel uploads gives near-optimal throughput.
const CHUNK_SIZE = 10 * 1024 * 1024;
const SINGLE_SHOT_LIMIT = 10 * 1024 * 1024;
const CONCURRENCY = 4;
const MAX_ATTEMPTS = 3;

const sessionKey = (f) => `vt_upload:${f.name}:${f.size}:${f.lastModified || 0}`;

async function runWithConcurrency(items, worker, limit) {
  const queue = items.slice();
  const workers = [];
  for (let i = 0; i < Math.min(limit, queue.length); i++) {
    workers.push(
      (async () => {
        while (queue.length) {
          const item = queue.shift();
          if (item === undefined) return;
          await worker(item);
        }
      })()
    );
  }
  await Promise.all(workers);
}

export default function UploadZone({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [stageLabel, setStageLabel] = useState("");
  const [retryInfo, setRetryInfo] = useState(null); // {chunkIndex, attempt}
  const [resumeState, setResumeState] = useState(null); // { videoId, uploaded, total }
  const [sourceLang, setSourceLang] = useState("auto");
  const [prompt, setPrompt] = useState("");
  const [turbo, setTurbo] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const abortRef = useRef(null); // AbortController

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }, []);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) setFile(f);
  };

  // Load resume state + source language when the file changes
  useEffect(() => {
    setResumeState(null);
    setRetryInfo(null);
    if (!file || file.size <= SINGLE_SHOT_LIMIT) return;
    const stored = localStorage.getItem(sessionKey(file));
    if (!stored) return;
    let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch {
      return;
    }
    if (!parsed?.videoId) return;

    // Restore user-selected source language + prompt from the session
    if (parsed.sourceLang) setSourceLang(parsed.sourceLang);
    if (parsed.prompt !== undefined) setPrompt(parsed.prompt || "");
    if (parsed.turbo !== undefined) setTurbo(!!parsed.turbo);

    (async () => {
      try {
        const { data } = await api.get(`/videos/upload/${parsed.videoId}/status`);
        if (data.status === "uploading") {
          setResumeState({
            videoId: parsed.videoId,
            uploaded: data.uploaded_chunks || [],
            total: data.total_chunks,
          });
        } else {
          localStorage.removeItem(sessionKey(file));
        }
      } catch {
        localStorage.removeItem(sessionKey(file));
      }
    })();
  }, [file]);

  // Keep sourceLang + prompt + turbo persisted per file whenever the user changes them
  useEffect(() => {
    if (!file || file.size <= SINGLE_SHOT_LIMIT) return;
    const stored = localStorage.getItem(sessionKey(file));
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      if (
        parsed.sourceLang !== sourceLang ||
        parsed.prompt !== prompt ||
        parsed.turbo !== turbo
      ) {
        localStorage.setItem(
          sessionKey(file),
          JSON.stringify({ ...parsed, sourceLang, prompt, turbo })
        );
      }
    } catch {
      /* ignore */
    }
  }, [sourceLang, prompt, turbo, file]);

  const persistSession = (theFile, patch) => {
    const key = sessionKey(theFile);
    const existing = localStorage.getItem(key);
    const base = existing ? (() => { try { return JSON.parse(existing); } catch { return {}; } })() : {};
    localStorage.setItem(key, JSON.stringify({ ...base, ...patch }));
  };

  const uploadSingleShot = async (theFile) => {
    const form = new FormData();
    form.append("file", theFile);
    form.append("source_lang", sourceLang);
    if (prompt.trim()) form.append("prompt", prompt.trim());
    form.append("turbo", turbo ? "true" : "false");
    const { data } = await api.post("/videos/upload", form, {
      headers: { "Content-Type": "multipart/form-data" },
      signal: abortRef.current?.signal,
      onUploadProgress: (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
      },
    });
    return data;
  };

  const uploadChunkWithRetry = async (url, blob, onChunkProgress, chunkIndex) => {
    let lastErr;
    for (let a = 1; a <= MAX_ATTEMPTS; a++) {
      if (abortRef.current?.signal.aborted) throw new Error("aborted");
      try {
        await api.put(url, blob, {
          headers: { "Content-Type": "application/octet-stream" },
          onUploadProgress: (evt) => onChunkProgress?.(evt.loaded || 0),
          timeout: 90000,
          signal: abortRef.current?.signal,
        });
        // Successful attempt — clear any retry message for this chunk
        setRetryInfo((prev) => (prev?.chunkIndex === chunkIndex ? null : prev));
        return;
      } catch (err) {
        if (abortRef.current?.signal.aborted) throw err;
        lastErr = err;
        if (a < MAX_ATTEMPTS) {
          setRetryInfo({ chunkIndex, attempt: a });
          await new Promise((r) => setTimeout(r, 800 * a));
        }
      }
    }
    throw lastErr;
  };

  const uploadChunked = async (theFile) => {
    const totalChunks = Math.ceil(theFile.size / CHUNK_SIZE);
    setStageLabel("PREPARING…");

    let videoId;
    let alreadyUploaded = new Set();

    if (resumeState?.videoId && resumeState.total === totalChunks) {
      videoId = resumeState.videoId;
      alreadyUploaded = new Set(resumeState.uploaded || []);
      setStageLabel(`RESUMING · ${alreadyUploaded.size}/${totalChunks} EXISTING`);
    } else {
      const { data: initData } = await api.post(
        "/videos/upload/init",
        {
          filename: theFile.name,
          source_lang: sourceLang,
          total_size: theFile.size,
          total_chunks: totalChunks,
          prompt: prompt.trim() || null,
          turbo,
        },
        { signal: abortRef.current?.signal }
      );
      videoId = initData.id;
      persistSession(theFile, { videoId, total: totalChunks, sourceLang, prompt, turbo });
    }

    const pending = [];
    for (let i = 0; i < totalChunks; i++) {
      if (!alreadyUploaded.has(i)) pending.push(i);
    }

    const perChunkProgress = new Map();
    const totalPre = alreadyUploaded.size * CHUNK_SIZE;

    const updateProgress = () => {
      let sum = 0;
      for (const v of perChunkProgress.values()) sum += v;
      const done = totalPre + sum;
      setProgress(Math.min(99, Math.round((done * 100) / theFile.size)));
    };

    let completed = alreadyUploaded.size;
    const worker = async (i) => {
      if (abortRef.current?.signal.aborted) throw new Error("aborted");
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, theFile.size);
      const chunk = theFile.slice(start, end);
      perChunkProgress.set(i, 0);
      await uploadChunkWithRetry(
        `/videos/upload/${videoId}/chunk/${i}`,
        chunk,
        (loaded) => {
          perChunkProgress.set(i, loaded);
          updateProgress();
        },
        i
      );
      perChunkProgress.set(i, chunk.size);
      completed += 1;
      updateProgress();
      setStageLabel(`UPLOADING · ${completed}/${totalChunks} CHUNKS`);
    };

    await runWithConcurrency(pending, worker, CONCURRENCY);

    setStageLabel("ASSEMBLING…");
    await api.post(`/videos/upload/${videoId}/finalize`);
    setProgress(100);

    localStorage.removeItem(sessionKey(theFile));

    const { data } = await api.get(`/videos/${videoId}`);
    return data;
  };

  const upload = async () => {
    if (!file) return;
    abortRef.current = new AbortController();
    setUploading(true);
    setProgress(0);
    setStageLabel("");
    setRetryInfo(null);
    try {
      const data =
        file.size <= SINGLE_SHOT_LIMIT
          ? await uploadSingleShot(file)
          : await uploadChunked(file);
      toast.success("Upload complete — transcription started");
      setFile(null);
      setProgress(0);
      setStageLabel("");
      setRetryInfo(null);
      setResumeState(null);
      onUploaded?.(data);
    } catch (err) {
      if (abortRef.current?.signal.aborted) {
        // silent cancel — a toast was already shown by cancelUpload
        return;
      }
      const msg = formatApiError(err);
      const suffix =
        file && file.size > SINGLE_SHOT_LIMIT
          ? " · Pick the same file again to resume."
          : "";
      toast.error(msg + suffix);
    } finally {
      setUploading(false);
      abortRef.current = null;
    }
  };

  const cancelUpload = async () => {
    if (!abortRef.current) return;
    abortRef.current.abort();
    setStageLabel("CANCELLING…");
    // Best-effort server cleanup
    if (file) {
      const stored = localStorage.getItem(sessionKey(file));
      if (stored) {
        try {
          const { videoId } = JSON.parse(stored);
          if (videoId) {
            await api.delete(`/videos/${videoId}`).catch(() => {});
          }
        } catch { /* ignore */ }
        localStorage.removeItem(sessionKey(file));
      }
    }
    setUploading(false);
    setProgress(0);
    setStageLabel("");
    setRetryInfo(null);
    setResumeState(null);
    toast.info("Upload cancelled. Server-side chunks were removed.");
  };

  const buttonLabel = () => {
    if (uploading) return "Uploading";
    if (resumeState && resumeState.uploaded.length > 0) {
      return `Resume (${resumeState.uploaded.length}/${resumeState.total})`;
    }
    return "Transcribe →";
  };

  return (
    <div className="card-surface p-6 lg:p-8 relative overflow-hidden" data-testid="upload-zone">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-primary font-mono mb-1">
            // NEW SESSION
          </div>
          <h2 className="text-3xl font-black tracking-tighter">Upload footage</h2>
        </div>
        <div className="text-[10px] font-mono tracking-widest text-muted-foreground">
          PARALLEL · RESUMABLE · CANCELLABLE
        </div>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        data-testid="upload-drop-target"
        className={`relative transition-colors duration-200 h-56 flex flex-col items-center justify-center text-center px-6 ${
          uploading ? "cursor-default" : "cursor-pointer"
        } ${
          dragOver
            ? "dashed-border bg-primary/5"
            : "border border-dashed border-white/15 bg-secondary/30 hover:bg-secondary/50"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={handleFile}
          data-testid="upload-file-input"
        />
        {file ? (
          <div className="flex flex-col items-center gap-2">
            <FileVideo className="w-8 h-8 text-primary" />
            <div className="font-mono text-sm max-w-md truncate">{file.name}</div>
            <div className="text-xs text-muted-foreground">
              {(file.size / (1024 * 1024)).toFixed(1)} MB
              {file.size > SINGLE_SHOT_LIMIT && (
                <>
                  {" · "}
                  <span className="text-primary">
                    {Math.ceil(file.size / CHUNK_SIZE)} chunks × {CONCURRENCY} parallel
                  </span>
                </>
              )}
            </div>
            {resumeState && resumeState.uploaded.length > 0 && !uploading && (
              <div
                data-testid="resume-indicator"
                className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest px-2 py-1 border border-primary/50 text-primary"
              >
                <RotateCw className="w-3 h-3" />
                RESUMABLE · {resumeState.uploaded.length}/{resumeState.total} CHUNKS ON SERVER
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <UploadCloud className="w-10 h-10 text-primary" />
            <div className="font-medium">Drag & drop a video here</div>
            <div className="text-xs text-muted-foreground">
              or click to browse — supports up to 1 hour of footage
            </div>
          </div>
        )}
      </div>

      {uploading && (
        <div className="mt-4" data-testid="upload-progress-area">
          <div className="flex justify-between text-[10px] font-mono tracking-widest mb-1">
            <span className="text-muted-foreground">{stageLabel || "UPLOADING"}</span>
            <span className="text-primary">{progress}%</span>
          </div>
          <div className="h-1 bg-secondary overflow-hidden">
            <div
              className="h-full bg-primary transition-[width] duration-200 glow-pulse"
              style={{ width: `${progress}%` }}
            />
          </div>
          {retryInfo && (
            <div
              data-testid="retry-indicator"
              className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest px-2 py-1 border border-amber-500/60 text-amber-400 bg-amber-500/10"
            >
              <AlertTriangle className="w-3 h-3" />
              CHUNK {retryInfo.chunkIndex} · RETRY {retryInfo.attempt}/{MAX_ATTEMPTS - 1}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 space-y-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] font-mono text-muted-foreground mb-2">
            TRANSCRIPTION HINT · OPTIONAL
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={uploading}
            data-testid="upload-prompt"
            rows={2}
            maxLength={800}
            placeholder="Add context to improve accuracy — e.g., speaker names, jargon, place names, or 'A Tamil film scene with English code-switching'."
            className="w-full rounded-none border border-white/10 bg-secondary/60 h-auto p-3 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/60 resize-y font-sans"
          />
          <div className="text-[10px] font-mono tracking-widest text-muted-foreground text-right mt-1">
            {prompt.length}/800 · GUIDES WHISPER VOCABULARY
          </div>
        </div>

        <label
          htmlFor="upload-turbo"
          className={`flex items-center justify-between gap-3 p-3 border cursor-pointer transition-colors ${
            turbo
              ? "border-primary/60 bg-primary/5"
              : "border-white/10 bg-secondary/40 hover:border-white/20"
          }`}
          data-testid="upload-turbo-label"
        >
          <div>
            <div className="text-[11px] font-mono tracking-widest text-foreground flex items-center gap-2">
              <span className={turbo ? "text-primary" : ""}>⚡ TURBO MODE</span>
              {turbo && (
                <span className="text-[9px] px-1.5 py-0.5 border border-primary/50 text-primary">
                  8× PARALLEL
                </span>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              Doubles the number of parallel Whisper workers (4 → 8). ~2× faster
              for long videos, ~2× the API cost.
            </div>
          </div>
          <input
            id="upload-turbo"
            type="checkbox"
            checked={turbo}
            onChange={(e) => setTurbo(e.target.checked)}
            disabled={uploading}
            data-testid="upload-turbo-toggle"
            className="w-4 h-4 accent-primary shrink-0"
          />
        </label>

        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
          <div className="flex-1">
            <div className="text-[10px] tracking-[0.3em] font-mono text-muted-foreground mb-2">
              SOURCE LANGUAGE
            </div>
            <Select value={sourceLang} onValueChange={setSourceLang} disabled={uploading}>
              <SelectTrigger
                data-testid="upload-source-lang"
                className="rounded-none border-white/10 bg-secondary/60 h-11"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANG_OPTIONS.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {uploading ? (
            <Button
              onClick={cancelUpload}
              data-testid="upload-cancel-button"
              variant="outline"
              className="rounded-none h-11 border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive font-semibold px-6"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel upload
            </Button>
          ) : (
            <Button
              onClick={upload}
              disabled={!file}
              data-testid="upload-submit-button"
              className="rounded-none h-11 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8"
            >
              {buttonLabel()}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
